import React, { useState } from "react";
import ContentButton from "../core/ui/ContentButton";
import { submitQuoteRequest } from "@webcore/utilities/GoogleFormSubmit.ts";
import { isValidEmail } from "@webcore/utilities/ValidationUtils.ts";

interface QuoteSectionProps {
  id?: string;
  eyebrow: React.ReactNode;
  heading: React.ReactNode;
  intro: React.ReactNode;
}

type SubmitStatus = "idle" | "submitting" | "success" | "error";

const QuoteSection: React.FC<QuoteSectionProps> = ({ id, eyebrow, heading, intro }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [request, setRequest] = useState("");
  const [showValidation, setShowValidation] = useState(false);
  const [status, setStatus] = useState<SubmitStatus>("idle");

  const nameError = showValidation && (name.trim().length === 0) ? "Please enter your name." : undefined;
  const emailError = showValidation && !isValidEmail(email.trim()) ? "Please enter a valid email address." : undefined;
  const requestError = showValidation && (request.trim().length === 0) ? "Please tell us a little about what you need." : undefined;

  const nameInput_change = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const emailInput_change = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const requestTextArea_change = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setRequest(e.target.value);
  };

  const quoteForm_submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") {
      return;
    }
    setShowValidation(true);

    const isValid = (name.trim().length > 0) && isValidEmail(email.trim()) && (request.trim().length > 0);
    if (!isValid) {
      return;
    }

    setStatus("submitting");
    try {
      await submitQuoteRequest({ name, email, request });
      setStatus("success");
    } catch {
      // Network failure (offline or blocked request) — surface an inline fallback with our
      // direct email instead of letting a public visitor hit the global error boundary.
      setStatus("error");
    }
  };

  return (
    <section id={id} className="cs-section cs-quote">
      <div className="cs-quote-inner">
        <div className="cs-quote-head">
          <div>
            <div className="eyebrow">{eyebrow}</div>
            <h2>{heading}</h2>
          </div>
          <div className="cs-quote-intro">{intro}</div>
        </div>

        {status === "success" ? (
          <div className="cs-quote-success" data-testid="cs-quote-success">
            <h3>Request received</h3>
            <p>
              Thanks — your request has been sent to our sales team. We&apos;ll reply to the email
              address you provided, usually within one business day.
            </p>
          </div>
        ) : (
          <form className="cs-quote-form" noValidate onSubmit={quoteForm_submit} data-testid="cs-quote-form">
            <div className="cs-field">
              <label className="cs-field-label" htmlFor="cs-quote-name">Name</label>
              <input
                id="cs-quote-name"
                className="cs-input"
                type="text"
                value={name}
                onChange={nameInput_change}
                placeholder="Your name"
                aria-invalid={nameError !== undefined}
              />
              {nameError && <span className="cs-field-error">{nameError}</span>}
            </div>
            <div className="cs-field">
              <label className="cs-field-label" htmlFor="cs-quote-email">Email</label>
              <input
                id="cs-quote-email"
                className="cs-input"
                type="email"
                value={email}
                onChange={emailInput_change}
                placeholder="you@company.com"
                aria-invalid={emailError !== undefined}
              />
              {emailError && <span className="cs-field-error">{emailError}</span>}
            </div>
            <div className="cs-field">
              <label className="cs-field-label" htmlFor="cs-quote-request">Request</label>
              <textarea
                id="cs-quote-request"
                className="cs-textarea"
                rows={4}
                value={request}
                onChange={requestTextArea_change}
                placeholder="Tell us about your business — which systems you'd like to connect, roughly how large your team is, and anything else that will help us prepare your quote."
                aria-invalid={requestError !== undefined}
              />
              {requestError && <span className="cs-field-error">{requestError}</span>}
            </div>
            {status === "error" && (
              <p className="cs-quote-failure">
                Something went wrong sending your request. Please try again, or email us directly
                at <a href="mailto:support@datafabriq.co">support@datafabriq.co</a>.
              </p>
            )}
            <ContentButton type="submit" variant="primary" size="lg">
              {status === "submitting" ? "Sending…" : "Request a quote"}
            </ContentButton>
          </form>
        )}
      </div>
    </section>
  );
};

export default QuoteSection;
